import { gql, useLazyQuery, useMutation } from '@apollo/client';
import { useEffect, useRef, useState } from 'react';
import { ActionSheet, Button, Toast } from 'antd-mobile';
import { CloseOutline, CloseCircleFill } from 'antd-mobile-icons';
import { Header } from './components/Header';
import { ScanInput } from './components/ScanInput';
import { Pda, useScanResult } from './components/Pda';
import { t } from '../../i18n';
import { ResultTable, createRecord, ScanRecord, addRecordWithLimit, useMaxRecords, Column } from './components/ResultTableNew';
import { playRepeatedScanSound, playScanFailedAoSound, playScanSound } from '../../utils/scanUtils';
import { parseException } from '../../utils/newExceptionUtils';
let currentCartonErrorToast: any = null;

const showCartonError = (message: string, onClose?: () => void) => {
    if (currentCartonErrorToast) {
        currentCartonErrorToast.close();
        currentCartonErrorToast = null;
    }
    currentCartonErrorToast = Toast.show({
        content: (
            <div className="relative py-2" style={{ minWidth: '200px', maxWidth: '80vw' }}>
                <div
                    className="absolute -top-2 right-0 cursor-pointer flex items-center justify-center w-6 h-6"
                    onClick={() => {
                        if (currentCartonErrorToast) {
                            currentCartonErrorToast.close();
                            currentCartonErrorToast = null;
                        }
                        onClose?.();
                    }}
                >
                    <span className="text-white text-lg">✕</span>
                </div>
                <div className="flex items-center justify-center mb-2">
                    <div className="flex items-center gap-2">
                        <CloseCircleFill fontSize={20} color='#c7381e' />
                        <div className="text-base">{t('pda.common.error')}</div>
                    </div>
                </div>
                <div className="text-sm text-center px-4" style={{ whiteSpace: 'pre-line' }}>{message}</div>
            </div>
        ),
        duration: 0,
    });
};

const clearCartonError = () => {
    if (currentCartonErrorToast) {
        currentCartonErrorToast.close();
        currentCartonErrorToast = null;
    }
};

interface ChannelOption {
    channelId: number;
    channelName: string;
}

interface ScanResult {
    success: boolean;
    needSelectChannel: boolean;
    channelOptions: ChannelOption[] | null;
    totalCount: number;
    onlineCount: number;
    messageKeyStr: string | null;
    channelName: string | null;
    clientCartonId: number | null;
}

interface ClientCartonScanResponse {
    handleClientCartonScan: ScanResult;
}

const HANDLE_CLIENT_CARTON_SCAN = gql`
    mutation HandleClientCartonScan($clientCartonNo: String!, $channelId: Long) {
        handleClientCartonScan(clientCartonNo: $clientCartonNo, channelId: $channelId) {
            success
            needSelectChannel
            channelOptions {
                channelId
                channelName
            }
            totalCount
            onlineCount
            messageKeyStr
            channelName
            clientCartonId
        }
    }
`;

const GET_CLIENT_CARTON_PREVIEW = gql`
    query GetClientCartonPreview($clientCartonNo: String!) {
        getClientCartonPreview(clientCartonNo: $clientCartonNo) {
            id
            channelId
            channelName
            scanStatus
            needSelectChannel
            channelOptions {
                id
                channelId
                channelName
                scanStatus
            }
        }
    }
`;

interface PreviewChannelOption {
    id: number | null;
    channelId: number;
    channelName: string;
    scanStatus: number;
}

interface ClientCartonPreviewResponse {
    getClientCartonPreview: {
        id: number | null;
        channelId: number | null;
        channelName: string | null;
        scanStatus: number;
        needSelectChannel: boolean;
        channelOptions: PreviewChannelOption[] | null;
    } | null;
}

const PageBody = () => {
    const [handleClientCartonScan] = useMutation<ClientCartonScanResponse>(HANDLE_CLIENT_CARTON_SCAN);
    const [fetchPreview] = useLazyQuery<ClientCartonPreviewResponse>(GET_CLIENT_CARTON_PREVIEW, { fetchPolicy: 'network-only' });
    const [cartonNo, setCartonNo] = useState('');
    const [lastResult, setLastResult] = useState<ScanResult | null>(null);
    const [previewChannelName, setPreviewChannelName] = useState<string | null>(null);
    const [lastBigBagInfoId, setLastBigBagInfoId] = useState<number | null>(null);
    const [scanning, setScanning] = useState(false);
    const [resultVisible, setResultVisible] = useState(false);
    const [channelSelectVisible, setChannelSelectVisible] = useState(false);
    const [channelOptions, setChannelOptions] = useState<PreviewChannelOption[]>([]);
    const [selectedChannelId, setSelectedChannelId] = useState<number | null>(null);

    const [successRecords, setSuccessRecords] = useState<ScanRecord[]>([]);
    const [failedRecords, setFailedRecords] = useState<ScanRecord[]>([]);
    const [totalSuccessCount, setTotalSuccessCount] = useState(0);
    const [totalFailedCount, setTotalFailedCount] = useState(0);
    const maxRecords = useMaxRecords();
    const resultMapRef = useRef<Map<string, { cartonNo: string; result: ScanResult; bigBagInfoId: number | null }>>(new Map());

    const [frameColor, setFrameColor] = useState<'green' | 'red' | 'orange' | null>(null);
    const clearFrame = () => setFrameColor(null);

    const scanResult = useScanResult();

    useEffect(() => {
        return () => {
            clearCartonError();
        };
    }, []);

    useEffect(() => {
        if (scanResult) {
            handleScan(scanResult);
        }
    }, [scanResult]);

    const handleScan = async (value: string) => {
        if (!value.trim()) return;
        setCartonNo(value.trim());
        await doScan(value.trim());
    };

    const doScan = async (clientCartonNo: string, channelId?: number) => {
        clearCartonError();
        try {
            setScanning(true);
            setLastResult(null);
            setPreviewChannelName(null);
            setFrameColor(null);

            const { data: previewData, error: previewError } = await fetchPreview({ variables: { clientCartonNo } });
            if (previewError) throw previewError;
            const preview = previewData?.getClientCartonPreview;

            if (!preview) {
                playScanFailedAoSound();
                setFrameColor('red');
                showCartonError(t('pda.pages.cartonScan.notFound'), clearFrame);
                setFailedRecords(prev => addRecordWithLimit(
                    createRecord({ value1: clientCartonNo, failedReason: t('pda.pages.cartonScan.notFound') }),
                    prev, maxRecords
                ));
                setTotalFailedCount(prev => prev + 1);
                return;
            }

            if (preview.needSelectChannel && preview.channelOptions?.length) {
                setCartonNo(clientCartonNo);
                setChannelOptions(preview.channelOptions);
                setSelectedChannelId(null);
                setChannelSelectVisible(true);
                return;
            }

            if (preview.scanStatus === 1) {
                playRepeatedScanSound();
                setFrameColor('red');
                showCartonError(t('pda.pages.cartonScan.alreadyScanned'), clearFrame);
                setFailedRecords(prev => addRecordWithLimit(
                    createRecord({ value1: clientCartonNo, failedReason: t('pda.pages.cartonScan.alreadyScanned') }),
                    prev, maxRecords
                ));
                setTotalFailedCount(prev => prev + 1);
                return;
            }

            setPreviewChannelName(preview.channelName);
            setLastBigBagInfoId(preview.id ?? null);
            setCartonNo(clientCartonNo);
            setResultVisible(true);

            const { data } = await handleClientCartonScan({
                variables: { clientCartonNo, channelId },
            });
            const result = data?.handleClientCartonScan;
            if (!result) {
                setResultVisible(false);
                return;
            }

            if (result.needSelectChannel && result.channelOptions?.length) {
                setResultVisible(false);
                const actions = result.channelOptions.map((opt) => ({
                    key: String(opt.channelId),
                    text: opt.channelName,
                }));
                ActionSheet.show({
                    actions,
                    onAction: async (action) => {
                        await doScan(clientCartonNo, Number(action.key));
                    },
                });
                return;
            }

            if (result.messageKeyStr === 'CLIENT_CARTON_ALREADY_SCANNED') {
                setResultVisible(false);
                playRepeatedScanSound();
                setFrameColor('red');
                showCartonError(t('pda.pages.cartonScan.alreadyScanned'), clearFrame);
                setFailedRecords(prev => addRecordWithLimit(
                    createRecord({ value1: clientCartonNo, failedReason: t('pda.pages.cartonScan.alreadyScanned') }),
                    prev, maxRecords
                ));
                setTotalFailedCount(prev => prev + 1);
                return;
            }

            if (result.success) {
                playScanSound();
                setFrameColor(result.messageKeyStr === 'CLIENT_CARTON_SCAN_SUCCESS_NEED_INDIVIDUAL' ? 'orange' : 'green');
                setLastResult(result);
                const cartonId = result.clientCartonId ?? preview.id ?? null;
                setLastBigBagInfoId(cartonId);
                const displayChannel = result.channelName ?? previewChannelName ?? '';
                const record = createRecord({ value1: clientCartonNo, value2: displayChannel });
                resultMapRef.current.set(record.id!, { cartonNo: clientCartonNo, result, bigBagInfoId: cartonId });
                setSuccessRecords(prev => addRecordWithLimit(record, prev, maxRecords));
                setTotalSuccessCount(prev => prev + 1);
            }

            if (!result.success && result.messageKeyStr) {
                setResultVisible(false);
                playScanFailedAoSound();
                setFrameColor('red');
                const errorMap: Record<string, string> = {
                    CLIENT_CARTON_NOT_FOUND: t('pda.pages.cartonScan.notFound'),
                    CLIENT_CARTON_PROCESSING: t('pda.pages.cartonScan.processing'),
                    CLIENT_CARTON_NO_PARCELS: t('pda.pages.cartonScan.noParcels'),
                    CLIENT_CARTON_SCAN_FAILED: t('pda.pages.cartonScan.scanFailed'),
                    TT_PRESORT_CONFIG_NOT_FOUND: t('pda.pages.cartonScan.presortConfigNotFound'),
                    // 后端把 BusinessException 转成 result.messageKeyStr 返回（不走 GraphQL error），
                    // 该路径不经过 parseException，未映射的 key 会裸显枚举名
                    DEPARTURE_SCAN_TT_BAG_NOT_HANDED_OVER: t('errors.business.pda.departureScanTtBagNotHandedOver.message'),
                };
                const errorMsg = errorMap[result.messageKeyStr] || result.messageKeyStr;
                showCartonError(errorMsg, clearFrame);
                setFailedRecords(prev => addRecordWithLimit(
                    createRecord({ value1: clientCartonNo, failedReason: errorMsg }),
                    prev, maxRecords
                ));
                setTotalFailedCount(prev => prev + 1);
            }
        } catch (e: any) {
            setResultVisible(false);
            playScanFailedAoSound();
            setFrameColor('red');
            // 后端 BusinessException（如尾程仓拦截"本地自有分拣仓未交接"）经 GraphQL error 携带
            // messageKey，用 parseException 解析三语明文；非 GraphQL 异常仍回退 e.message
            const errorMsg = e?.graphQLErrors?.length
                ? parseException(e).message
                : (e.message || 'Scan failed');
            showCartonError(errorMsg, clearFrame);
            setFailedRecords(prev => addRecordWithLimit(
                createRecord({ value1: cartonNo || 'unknown', failedReason: errorMsg }),
                prev, maxRecords
            ));
            setTotalFailedCount(prev => prev + 1);
        } finally {
            setScanning(false);
        }
    };

    const handleClose = () => {
        setResultVisible(false);
        setLastResult(null);
        setPreviewChannelName(null);
        setFrameColor(null);
    };

    const handleChannelConfirm = async () => {
        if (!selectedChannelId) return;
        const selected = channelOptions.find(o => o.channelId === selectedChannelId);
        if (!selected) return;
        setChannelSelectVisible(false);

        if (selected.scanStatus === 1) {
            playRepeatedScanSound();
            setFrameColor('orange');
            showCartonError(t('pda.pages.cartonScan.alreadyScanned'), clearFrame);
            setFailedRecords(prev => addRecordWithLimit(
                createRecord({ value1: cartonNo, failedReason: t('pda.pages.cartonScan.alreadyScanned') }),
                prev, maxRecords
            ));
            setTotalFailedCount(prev => prev + 1);
            return;
        }

        setPreviewChannelName(selected.channelName);
        setLastBigBagInfoId(selected.id);
        setResultVisible(true);
        setLastResult(null);
        setScanning(true);

        try {
            const { data } = await handleClientCartonScan({
                variables: { clientCartonNo: cartonNo, channelId: selectedChannelId },
            });
            const result = data?.handleClientCartonScan;
            if (!result) {
                setResultVisible(false);
                return;
            }

            if (result.success) {
                playScanSound();
                setFrameColor(result.messageKeyStr === 'CLIENT_CARTON_SCAN_SUCCESS_NEED_INDIVIDUAL' ? 'orange' : 'green');
                setLastResult(result);
                const cartonId = result.clientCartonId ?? selected.id ?? null;
                setLastBigBagInfoId(cartonId);
                const record = createRecord({ value1: cartonNo, value2: selected.channelName });
                resultMapRef.current.set(record.id!, { cartonNo, result, bigBagInfoId: cartonId });
                setSuccessRecords(prev => addRecordWithLimit(record, prev, maxRecords));
                setTotalSuccessCount(prev => prev + 1);
            }

            if (!result.success && result.messageKeyStr) {
                setResultVisible(false);
                playScanFailedAoSound();
                setFrameColor('red');
                const errorMap: Record<string, string> = {
                    CLIENT_CARTON_NOT_FOUND: t('pda.pages.cartonScan.notFound'),
                    CLIENT_CARTON_PROCESSING: t('pda.pages.cartonScan.processing'),
                    CLIENT_CARTON_NO_PARCELS: t('pda.pages.cartonScan.noParcels'),
                    CLIENT_CARTON_SCAN_FAILED: t('pda.pages.cartonScan.scanFailed'),
                    TT_PRESORT_CONFIG_NOT_FOUND: t('pda.pages.cartonScan.presortConfigNotFound'),
                    // 后端把 BusinessException 转成 result.messageKeyStr 返回（不走 GraphQL error），
                    // 该路径不经过 parseException，未映射的 key 会裸显枚举名
                    DEPARTURE_SCAN_TT_BAG_NOT_HANDED_OVER: t('errors.business.pda.departureScanTtBagNotHandedOver.message'),
                };
                const errorMsg = errorMap[result.messageKeyStr] || result.messageKeyStr;
                showCartonError(errorMsg, clearFrame);
                setFailedRecords(prev => addRecordWithLimit(
                    createRecord({ value1: cartonNo, failedReason: errorMsg }),
                    prev, maxRecords
                ));
                setTotalFailedCount(prev => prev + 1);
            }
        } catch (e: any) {
            setResultVisible(false);
            playScanFailedAoSound();
            setFrameColor('red');
            // 后端 BusinessException（如尾程仓拦截"本地自有分拣仓未交接"）经 GraphQL error 携带
            // messageKey，用 parseException 解析三语明文；非 GraphQL 异常仍回退 e.message
            const errorMsg = e?.graphQLErrors?.length
                ? parseException(e).message
                : (e.message || 'Scan failed');
            showCartonError(errorMsg, clearFrame);
            setFailedRecords(prev => addRecordWithLimit(
                createRecord({ value1: cartonNo, failedReason: errorMsg }),
                prev, maxRecords
            ));
            setTotalFailedCount(prev => prev + 1);
        } finally {
            setScanning(false);
        }
    };

    const handleSuccessRowClick = (record: ScanRecord) => {
        const stored = resultMapRef.current.get(record.id!);
        if (!stored) return;
        setCartonNo(stored.cartonNo);
        setLastResult(stored.result);
        setLastBigBagInfoId(stored.bigBagInfoId);
        setResultVisible(true);
    };

    const statusTitle = () => {
        if (!lastResult) return '';
        switch (lastResult.messageKeyStr) {
            case 'CLIENT_CARTON_BATCH_INBOUND_SUCCESS':
                return t('pda.pages.cartonScan.inboundCompleted');
            case 'PRESORT_CLIENT_CARTON_SCAN_SUCCESS':
                return t('pda.pages.cartonScan.presortInboundCompleted');
            case 'CLIENT_CARTON_SCAN_SUCCESS_NEED_INDIVIDUAL':
                return t('pda.pages.cartonScan.needIndividualScan');
            default:
                return t('pda.pages.cartonScan.scanSuccess');
        }
    };

    const successColumns: Column[] = [
        { title: t('pda.pages.cartonScan.cartonNo'), key: 'value1' },
        { title: t('pda.common.time'), key: 'time' },
    ];

    const failedColumns: Column[] = [
        { title: t('pda.pages.cartonScan.cartonNo'), key: 'value1' },
        { title: t('pda.common.time'), key: 'time' },
        { title: t('pda.common.failedReason'), key: 'failedReason' },
    ];

    const frameColorClass = frameColor === 'green'
        ? 'border-green-500'
        : frameColor === 'orange'
            ? 'border-orange-500'
            : '';
    const frameStyle = frameColor === 'red' ? { borderColor: '#c7381e' } : undefined;

    return (
        <div className="p-4">
            {frameColor && (
                <div className={`fixed inset-0 border-8 pointer-events-none z-[1100] ${frameColorClass}`} style={frameStyle} />
            )}

            <ScanInput
                placeholder={t('pda.pages.cartonScan.placeholder')}
                value={cartonNo}
                onScan={handleScan}
                onChange={setCartonNo}
            />

            <ResultTable
                successRecords={successRecords}
                failedRecords={failedRecords}
                successColumns={successColumns}
                failedColumns={failedColumns}
                totalSuccessCount={totalSuccessCount}
                totalFailedCount={totalFailedCount}
                onSuccessRowClick={handleSuccessRowClick}
                successClickableKey="value1"
            />

            {resultVisible && (
                <div className="fixed inset-0 z-50 bg-white flex flex-col">
                    <div className="relative flex items-center px-4 pt-4 pb-8">
                        <span className="text-lg text-gray-800 truncate text-center w-full px-10">{cartonNo}</span>
                        <CloseOutline className="absolute right-4 top-4 text-xl cursor-pointer" onClick={handleClose} />
                    </div>

                    <div className="flex flex-col items-center px-6">
                        <div className="text-2xl font-bold mb-2">{t('pda.pages.cartonScan.scanSuccess')}</div>
                        {(lastResult?.channelName ?? previewChannelName) && (
                            <div className="text-black text-2xl">{lastResult?.channelName ?? previewChannelName}</div>
                        )}
                    </div>

                    <div className="flex-1 flex flex-col items-center justify-center px-6 pb-16">
                        {!lastResult ? (
                            <div className="text-xl font-bold py-6">
                                {t('pda.pages.cartonScan.inbounding')}
                            </div>
                        ) : lastResult.success ? (
                            <>
                                <div className="text-3xl font-bold mb-2">{statusTitle()}</div>
                                <div className="mb-6" />
                                {(lastResult.messageKeyStr === 'CLIENT_CARTON_BATCH_INBOUND_SUCCESS'
                                    || lastResult.messageKeyStr === 'PRESORT_CLIENT_CARTON_SCAN_SUCCESS'
                                    || lastResult.messageKeyStr === 'CLIENT_CARTON_SCAN_SUCCESS_NEED_INDIVIDUAL') && (
                                    <table className="border-collapse border border-gray-300 text-lg table-fixed">
                                        <colgroup>
                                            <col className="w-1/2" />
                                            <col className="w-1/2" />
                                        </colgroup>
                                        <tbody>
                                            <tr>
                                                <td className="border border-gray-300 px-3 py-3 text-center whitespace-nowrap">{t('pda.pages.cartonScan.parcelCount')}</td>
                                                <td className="border border-gray-300 px-3 py-3 text-center">
                                                    <span className="font-bold">{lastResult.totalCount}</span>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                )}
                            </>
                        ) : null}
                    </div>

                    <div className="p-4" style={{ '--adm-color-primary': '#1677ff' } as React.CSSProperties}>
                        <Button block color="primary" onClick={handleClose}>
                            {t('pda.pages.cartonScan.close')}
                        </Button>
                    </div>
                </div>
            )}

            {channelSelectVisible && (
                <div className="fixed inset-0 z-50 bg-white flex flex-col">
                    <div className="relative flex items-center p-4">
                        <span className="text-sm font-bold text-black truncate text-center w-full px-10">{cartonNo}</span>
                        <CloseOutline className="absolute right-4 text-lg cursor-pointer" onClick={() => setChannelSelectVisible(false)} />
                    </div>

                    <div className="flex-1 flex flex-col items-center px-6 pt-4">
                        <div className="text-2xl font-bold mb-4">{t('pda.pages.cartonScan.selectChannel')}</div>
                        <div className="mb-4" />
                        <div className="w-4/5 space-y-3">
                            {channelOptions.map(opt => (
                                <div
                                    key={opt.channelId}
                                    className={`border rounded-lg p-4 flex items-center cursor-pointer ${selectedChannelId === opt.channelId ? 'border-blue-500' : 'border-gray-200'}`}
                                    onClick={() => setSelectedChannelId(opt.channelId)}
                                >
                                    <div className={`w-5 h-5 rounded-full border-2 mr-3 flex items-center justify-center ${selectedChannelId === opt.channelId ? 'border-blue-500' : 'border-gray-300'}`}>
                                        {selectedChannelId === opt.channelId && <div className="w-3 h-3 rounded-full bg-blue-500" />}
                                    </div>
                                    <span className="flex-1 text-black font-bold text-2xl text-center">{opt.channelName}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="p-4">
                        <Button block color="primary" disabled={!selectedChannelId} onClick={handleChannelConfirm}>
                            {t('pda.pages.cartonScan.confirm')}
                        </Button>
                    </div>
                </div>
            )}

        </div>
    );
};

export default function CartonScanPage() {
    return (
        <div className="w-full h-full flex flex-col gap-2 p-2">
            <Header title={t('pda.pages.cartonScan.cartonScanTitle')} />
            <Pda />
            <PageBody />
        </div>
    );
}
